import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import FormFieldset from 'components/forms/form/FormFieldset';
import { ModalBody } from 'react-bootstrap';
import useFetchCustomTemplateByUid from 'components/domain/automation/customTemplates/useFetchCustomTemplateByUid';
import FormWithProvider from 'components/forms/form/Form';
import { MessagingType } from 'models/automation/CustomTemplates';
import { UsageType } from 'models/automation/Common';
import { useSearchParams } from 'react-router-dom';
import { LanguageCode } from 'i18n/internationalization.types';
import useAvailableLanguages from 'pages/automations/common/useAvailableLanguages';
import { TRANSLATE_CUSTOM_TEMPLATE_MUTATION_KEY } from 'components/domain/automation/customTemplates/useTranslateCustomTemplateMutation';
import { useIsMutating } from '@tanstack/react-query';
import {
  customTemplateAddEditFormSchema,
  getFormDefaultValues,
} from './CustomTemplateAddEditModal.schema';
import CustomTemplateAddEditModalFormFields from './CustomTemplateAddEditModalFormFields';
import useCustomTemplateFormSubmit from './useCustomTemplateFormSubmit';
import CustomTemplateAddEditModalFooter from './CustomTemplateAddEditModalFooter';
import { TEMPLATE_TYPE_QUERY_PARAM } from './CustomTemplateAddEditModal.constants';

// TODO: make this dependent on the template settings, to be done after Manage Languages modal is implemented
const REQUIRE_ALL_LANGUAGES = false;

const CustomTemplateAddEditModal = ({
  customTemplateUid,
  closeModal,
}: {
  customTemplateUid?: string;
  closeModal: () => void;
}) => {
  const [searchParams] = useSearchParams();
  const isEditMode = !!customTemplateUid;
  const { availableLanguages, isLoading: isLoadingAvailableLanguages } =
    useAvailableLanguages(UsageType.CUSTOM);
  const { data: customTemplate, isLoading: isLoadingCustomTemplate } =
    useFetchCustomTemplateByUid({
      customTemplateUid,
      enabled: isEditMode,
    });
  const isTranslating =
    useIsMutating({
      mutationKey: [TRANSLATE_CUSTOM_TEMPLATE_MUTATION_KEY],
    }) > 0;
  const { handleSubmit } = useCustomTemplateFormSubmit();

  if (
    (isLoadingCustomTemplate && customTemplateUid) ||
    isLoadingAvailableLanguages
  ) {
    return <LoadingPlaceholder />;
  }

  const templateTypeParam = searchParams.get(TEMPLATE_TYPE_QUERY_PARAM);

  return (
    <FormWithProvider
      horizontal
      defaultValues={getFormDefaultValues({
        customTemplate,
        availableLanguages,
        templateType: templateTypeParam as MessagingType,
        currentLanguage: LanguageCode.EN,
      })}
      onSubmit={handleSubmit}
      schema={customTemplateAddEditFormSchema({
        availableLanguages,
        messagingType: templateTypeParam as MessagingType,
        requireAllLanguages: REQUIRE_ALL_LANGUAGES,
      })}
      dirtyChecker
      dirtyCheckerOptions={{
        skipFields: ['language'],
      }}
      noValidate
      key={templateTypeParam}
    >
      <FormFieldset disabled={isTranslating}>
        <ModalBody>
          <CustomTemplateAddEditModalFormFields
            availableLanguages={availableLanguages}
          />
        </ModalBody>
        <CustomTemplateAddEditModalFooter
          closeModal={closeModal}
          customTemplate={customTemplate}
        />
      </FormFieldset>
    </FormWithProvider>
  );
};

CustomTemplateAddEditModal.defaultProps = {
  customTemplateUid: undefined,
};

export default CustomTemplateAddEditModal;
